import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  ArrowRight, Building2, Users, Globe, Award, Play, ChevronDown,
  Monitor, School, Heart, Shield, Cpu, HardHat, Star, Quote
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/utils/cn'

gsap.registerPlugin(ScrollTrigger)

// Stats Data
const stats = [
  { value: '₹150+', label: 'Crore Business Ecosystem' },
  { value: '11', label: 'Group Companies' },
  { value: '50+', label: 'Districts Covered' },
  { value: '7+', label: 'Years Experience' },
]

// Services Data
const services = [
  {
    icon: School,
    title: 'Furniture Solutions',
    description: 'Premium school, college, hospital & office furniture with ergonomic designs and durable quality.',
    href: '/services/furniture',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Monitor,
    title: 'Smart Education',
    description: 'Transform classrooms with interactive displays, digital learning & e-learning infrastructure.',
    href: '/services/smart-education',
    color: 'from-purple-500 to-pink-500',
  },
  {
    icon: Cpu,
    title: 'Digital Transformation',
    description: 'Enterprise software, web applications, mobile apps & AI-powered solutions for businesses.',
    href: '/services/digital',
    color: 'from-green-500 to-emerald-500',
  },
  {
    icon: Heart,
    title: 'Healthcare Solutions',
    description: 'Medical infrastructure, nutrition programs & central kitchen setup for institutions.',
    href: '/services/healthcare',
    color: 'from-red-500 to-orange-500',
  },
  {
    icon: HardHat,
    title: 'Infrastructure Development',
    description: 'Vinyl flooring, playgrounds, gym infrastructure & complete institutional development.',
    href: '/services/infrastructure',
    color: 'from-yellow-500 to-amber-500',
  },
  {
    icon: Shield,
    title: 'Security Solutions',
    description: 'CCTV, surveillance systems & monitoring platforms for comprehensive security coverage.',
    href: '/services/security',
    color: 'from-indigo-500 to-violet-500',
  },
]

// Group Companies - All 11 companies
const groupCompanies = [
  { name: 'GSM Corp', focus: 'Core Business Operations' },
  { name: 'GPMM Enterprises', focus: 'Manufacturing & Trading' },
  { name: 'Ved Enterprises', focus: 'Educational Supplies' },
  { name: 'Adhya Enterprises', focus: 'Healthcare Solutions' },
  { name: 'ADMS Technoways', focus: 'Technology Services' },
  { name: 'Enteka Systems Pvt Ltd', focus: 'System Integration' },
  { name: 'Mandate IT Solutions Pvt Ltd', focus: 'IT Infrastructure' },
  { name: 'Aarambh Enterprises', focus: 'Startup Initiatives' },
  { name: 'VSM Enterprises', focus: 'Distribution Network' },
  { name: 'Eonixa Dynamics Pvt Ltd', focus: 'Innovation & Technology' },
  { name: 'AHAD Solutions Pvt Ltd', focus: 'Specialized Solutions' },
]

// Industries
const industries = [
  { icon: Building2, name: 'Government', count: '100+ Projects' },
  { icon: School, name: 'Education', count: '200+ Schools' },
  { icon: Heart, name: 'Healthcare', count: '50+ Hospitals' },
  { icon: Users, name: 'Corporate', count: '150+ Clients' },
  { icon: Globe, name: 'Smart Cities', count: '20+ Cities' },
]

// Testimonials
const testimonials = [
  {
    quote: "GSM Corporation delivered exceptional quality in our smart classroom project. Their attention to detail and commitment to timelines is commendable.",
    author: 'Dr. Rajesh Kumar',
    role: 'Principal, Govt. College',
    rating: 5,
  },
  {
    quote: "Professional approach, quality products, and excellent after-sales support. GSM has been our trusted partner for furniture procurement.",
    author: 'Suresh Patil',
    role: 'Director, Municipal Corporation',
    rating: 5,
  },
  {
    quote: "The digital transformation solution provided by GSM has significantly improved our operational efficiency. Highly recommended.",
    author: 'Priya Sharma',
    role: 'IT Manager, District Office',
    rating: 5,
  },
]

export default function Home() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 500], [0, 150])
  const opacity = useTransform(scrollY, [0, 300], [1, 0])

  useEffect(() => {
    // GSAP Animations
    const ctx = gsap.context(() => {
      // Stats counter animation
      const statElements = gsap.utils.toArray('.stat-number') as Element[]
      statElements.forEach((stat) => {
        gsap.from(stat, {
          scrollTrigger: {
            trigger: stat,
            start: 'top 80%',
            once: true,
          },
          textContent: 0,
          duration: 2,
          ease: 'power1.inOut',
          snap: { textContent: 1 },
        })
      })

      // Service cards animation
      const cardElements = gsap.utils.toArray('.service-card') as Element[]
      cardElements.forEach((card, index) => {
        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            once: true,
          },
          opacity: 0,
          y: 50,
          duration: 0.6,
          delay: index * 0.1,
        })
      })
    })

    return () => ctx.revert()
  }, [])

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-graphite-950 to-graphite-950" />
          <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-5" />
          <motion.div
            style={{ y }}
            className="absolute inset-0 bg-[url('/images/hero-bg.jpg')] bg-cover bg-center opacity-20"
          />

          {/* Animated Grid */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-transparent to-secondary-500/10" />
            <div className="absolute inset-0 backdrop-blur-[100px]" />
          </div>

          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/20 rounded-full filter blur-[100px] animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-500/20 rounded-full filter blur-[100px] animate-pulse" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-accent-500/10 rounded-full filter blur-[80px] animate-pulse" />
        </div>

        {/* Content */}
        <div className="relative container-premium py-20 md:py-32">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
            >
              <Award className="w-4 h-4 text-accent-400" />
              <span className="text-sm text-graphite-300">Trusted by 500+ Institutions Across India</span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="heading-xl text-white mb-6"
            >
              <span className="gradient-text">Transforming</span>
              <br />
              Government & Institutional
              <br />
              Infrastructure Across{' '}
              <span className="gradient-text-gold">India</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-graphite-300 mb-12 max-w-3xl mx-auto leading-relaxed"
            >
              Premium solutions for Smart Classrooms, Healthcare Infrastructure,
              Digital Transformation & Educational Ecosystem Development.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
            >
              <Button variant="gold" size="lg" asChild>
                <Link to="/contact">
                  Request Proposal
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/services">
                  Explore Services
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </motion.div>

            {/* Video Button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex items-center justify-center gap-4"
            >
              <button className="flex items-center gap-3 text-graphite-400 hover:text-white transition-colors group">
                <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center group-hover:bg-white/20 group-hover:border-accent-500/50 transition-all">
                  <Play className="w-5 h-5 text-accent-400 ml-1" />
                </div>
                <span className="text-sm">Watch Our Story</span>
              </button>
            </motion.div>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            style={{ opacity }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <div className="flex flex-col items-center gap-2 animate-bounce">
              <span className="text-xs text-graphite-500">Scroll</span>
              <ChevronDown className="w-5 h-5 text-primary-400" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 border-y border-white/5">
        <div className="container-premium">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="stats-number stat-number">{stat.value}</div>
                <div className="text-graphite-400 mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
                <span className="text-primary-400 text-sm font-medium">About GSM Corporation</span>
              </div>
              <h2 className="heading-lg text-white mb-6">
                Building India's Future,
                <br />
                <span className="gradient-text">One Institution at a Time</span>
              </h2>
              <p className="text-lg text-graphite-300 mb-6 leading-relaxed">
                Established in 2017, GSM Corporation has emerged as a leading provider of comprehensive
                infrastructure solutions for government bodies, educational institutions, and healthcare
                organizations across India.
              </p>
              <p className="text-graphite-400 mb-8">
                Our group of companies delivers integrated solutions spanning smart classrooms,
                institutional furniture, healthcare infrastructure, digital transformation, and
                security systems — all designed to elevate institutional capabilities and drive
                meaningful impact.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button variant="primary" asChild>
                  <Link to="/about">
                    Our Story
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button variant="ghost" asChild>
                  <Link to="/group-companies">Group Companies</Link>
                </Button>
              </div>
            </motion.div>

            {/* Image Grid */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="glass-card-premium h-48 overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-primary-500/30 to-secondary-500/30 flex items-center justify-center">
                      <School className="w-16 h-16 text-primary-400" />
                    </div>
                  </div>
                  <div className="glass-card-premium h-32 overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-accent-500/30 to-primary-500/30 flex items-center justify-center">
                      <Users className="w-12 h-12 text-accent-400" />
                    </div>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="glass-card-premium h-32 overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-secondary-500/30 to-primary-500/30 flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-secondary-400" />
                    </div>
                  </div>
                  <div className="glass-card-premium h-48 overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-primary-500/30 to-accent-500/30 flex items-center justify-center">
                      <Award className="w-16 h-16 text-primary-400" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent-500/20 rounded-full filter blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary-500/20 rounded-full filter blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gradient-to-b from-graphite-950 via-primary-950/20 to-graphite-950">
        <div className="container-premium">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-500/10 border border-secondary-500/20 mb-6"
            >
              <span className="text-secondary-400 text-sm font-medium">Our Services</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-lg text-white mb-4"
            >
              Comprehensive Solutions for
              <br />
              <span className="gradient-text-cyan">Every Institutional Need</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-graphite-400 max-w-3xl mx-auto"
            >
              From smart classrooms to healthcare infrastructure, we deliver end-to-end
              solutions tailored to government, education, and corporate requirements.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={service.href} className="block h-full">
                  <Card variant="hover" className="h-full p-6 group cursor-pointer">
                    <div className={cn(
                      "w-14 h-14 rounded-xl mb-4 flex items-center justify-center bg-gradient-to-br",
                      service.color
                    )}>
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    <CardContent className="p-0">
                      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-primary-400 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-graphite-400 mb-4">
                        {service.description}
                      </p>
                      <div className="flex items-center gap-2 text-primary-400 group-hover:text-primary-300 transition-colors">
                        <span className="text-sm font-medium">Learn More</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button variant="outline" size="lg" asChild>
              <Link to="/services">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-md text-white mb-4"
            >
              Industries We <span className="gradient-text">Serve</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-graphite-400 text-lg"
            >
              Trusted partner across diverse sectors
            </motion.p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to="/industries"
                  className="block glass-card-premium p-6 text-center hover:border-primary-500/30 transition-all group"
                >
                  <industry.icon className="w-10 h-10 mx-auto mb-4 text-primary-400 group-hover:text-primary-300 transition-colors" />
                  <h3 className="text-white font-semibold mb-2">{industry.name}</h3>
                  <p className="text-sm text-graphite-500">{industry.count}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Group Companies Section */}
      <section className="section-padding bg-gradient-to-b from-graphite-950 via-secondary-950/20 to-graphite-950">
        <div className="container-premium">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6"
            >
              <span className="text-accent-400 text-sm font-medium">Group Ecosystem</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-lg text-white mb-4"
            >
              Our <span className="gradient-text-gold">Group Companies</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-graphite-400 max-w-3xl mx-auto"
            >
              A diverse ecosystem of specialized companies working together to deliver
              comprehensive solutions across sectors.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupCompanies.map((company, index) => (
              <motion.div
                key={company.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="hover" className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">
                        {company.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{company.name}</h3>
                      <p className="text-sm text-graphite-400">{company.focus}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button variant="gold" size="lg" asChild>
              <Link to="/group-companies">
                Explore Group Ecosystem
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-md text-white mb-4"
            >
              What Our <span className="gradient-text">Clients Say</span>
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="premium" className="p-8 h-full">
                  <Quote className="w-10 h-10 text-primary-500/30 mb-4" />
                  <p className="text-graphite-300 mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-accent-400 text-accent-400" />
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-sm text-graphite-500">{testimonial.role}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-premium">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-secondary-600 to-accent-600" />
            <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-10" />

            <div className="relative px-8 py-16 md:px-16 md:py-24 text-center">
              <h2 className="heading-lg text-white mb-6">
                Ready to Transform Your Institution?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Partner with GSM Corporation for comprehensive infrastructure solutions
                that drive growth and excellence.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button variant="gold" size="xl" asChild>
                  <Link to="/contact">
                    Get Started Today
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button variant="ghost" size="xl" asChild className="bg-white/10 border-white/20">
                  <Link to="/franchise">Become a Partner</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}